import React from 'react';
import block from 'propmods';
import { TextInput, Button } from 'lego-on-react';
import times from 'lodash/times';
import clamp from 'lodash/clamp';
import Caption from './Caption';
import { Field, Subfield } from './Form';

import './FieldList.scss';

const b = block('ManualFieldList');

interface InnerInputProps<T> {
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    onBlur?: () => void;
}

type FieldListType = 'auto' | 'manual';

type DefaultValueType<T, U extends FieldListType> = U extends 'manual'
    ? {
          defaultValue: T;
      }
    : {
          defaultValue?: T;
      };

type ListItemsProps<T, U extends FieldListType> = {
    values: T[];
    errors?: string[];
    minCount: number;
    maxCount: number;
    disabled?: boolean;
    onChange: (values: T[]) => void;
    renderInput: (props: InnerInputProps<T>, i: number) => React.ReactNode;
    displayFirstRemoveButton?: boolean;
    disableFirstRemoveButton?: boolean;
} & DefaultValueType<T, U>;

function ListItemsManual<T>({
    values,
    errors = [],
    minCount,
    maxCount,
    disabled,
    onChange,
    defaultValue,
    renderInput,
    displayFirstRemoveButton,
    disableFirstRemoveButton,
}: ListItemsProps<T, 'manual'>) {
    const onFieldAdd = () => {
        onChange(values.length === 0 && minCount !== 0 ? [defaultValue, defaultValue] : [...values, defaultValue]);
    };

    const onFieldRemoveAt = (n: number) => () => {
        const newValues = [...values];
        newValues.splice(n, 1);
        onChange(newValues);
    };

    const onChangeAt = (n: number) => (value: T) => {
        const newValues = [...values];
        newValues[n] = value;
        onChange(newValues);
    };

    const fieldCount = clamp(values.length, minCount, maxCount);

    const shoulDisableRemoveButton = (i: number) => i === 0 && (disableFirstRemoveButton || fieldCount <= minCount);

    return (
        <div {...b()}>
            {times(fieldCount, (i) => (
                <Subfield error={errors[i]} key={i}>
                    <div {...b('input-container')}>
                        {renderInput(
                            {
                                disabled,
                                value: values[i] || defaultValue,
                                onChange: onChangeAt(i),
                            },
                            i,
                        )}
                        {(displayFirstRemoveButton || i !== 0) && (
                            <div {...b('button-rm')}>
                                <Button
                                    theme="normal"
                                    size="s"
                                    onClick={onFieldRemoveAt(i)}
                                    disabled={shoulDisableRemoveButton(i) || disabled}
                                >
                                    –
                                </Button>
                            </div>
                        )}
                    </div>
                </Subfield>
            ))}
            {fieldCount < maxCount && (
                <div {...b('button-add')}>
                    <Button theme="normal" size="s" onClick={onFieldAdd} disabled={disabled}>
                        +
                    </Button>
                </div>
            )}
        </div>
    );
}

function ListItemsAuto<T>({
    minCount,
    maxCount,
    onChange,
    values = [],
    errors = [],
    disabled = false,
    renderInput,
}: ListItemsProps<T, 'auto'>) {
    const onChangeAt = (index: number) => (value: T) => {
        const newValues = [...values];
        newValues[index] = value;

        onChange(newValues);
    };

    const onBlur = () => {
        onChange(values.slice(0, values.map(Boolean).lastIndexOf(true) + 1));
    };

    const count = clamp(values.length + 1, minCount, maxCount);

    return (
        <div>
            {times(count, (i) => (
                <Subfield error={errors[i]} key={i}>
                    {renderInput(
                        {
                            disabled,
                            onBlur,
                            onChange: onChangeAt(i),
                            value: values[i],
                        },
                        i,
                    )}
                </Subfield>
            ))}
        </div>
    );
}

export const renderTextInput = ({ value, ...restProps }: InnerInputProps<string>) => (
    <TextInput theme="normal" size="s" text={value} {...restProps} />
);

type Props<T, U extends FieldListType> = {
    values: T[];
    errors?: string[];
    name?: string;
    label: React.ReactNode;
    minCount: number;
    maxCount: number;
    onChange: (values: T[]) => void;
    disabled?: boolean;
    caption?: React.ReactNode;
    description?: React.ReactNode;
    documentationUrl?: string;
    required?: boolean;
    listType: U;
    renderInput: (props: InnerInputProps<T>, i: number) => React.ReactNode;
    displayFirstRemoveButton?: boolean;
    disableFirstRemoveButton?: boolean;
    wide?: boolean;
} & DefaultValueType<T, U>;

function FieldList<T, U extends FieldListType>({
    label,
    required,
    caption,
    description,
    documentationUrl,
    listType,
    defaultValue,
    displayFirstRemoveButton,
    disableFirstRemoveButton,
    wide,
    ...commonProps
}: Props<T, U>) {
    return (
        <Field label={label} required={required} wide={wide}>
            {listType === 'manual' ? (
                <ListItemsManual
                    defaultValue={defaultValue!}
                    displayFirstRemoveButton={displayFirstRemoveButton}
                    disableFirstRemoveButton={disableFirstRemoveButton}
                    {...commonProps}
                />
            ) : (
                <ListItemsAuto {...commonProps} />
            )}
            {caption && <Caption url={documentationUrl}>{caption}</Caption>}
            {description || null}
        </Field>
    );
}

export default FieldList;
